import { useState, useEffect, useContext, createContext } from 'react';
import { useApp } from '../context/AppContext';

// Typy ról
export type UserRole = 'admin' | 'user' | 'client';

// Interfejs uprawnień
interface Permissions {
  // Zadania
  'tasks.view': boolean;
  'tasks.create': boolean;
  'tasks.edit': boolean;
  'tasks.delete': boolean;
  'tasks.assign': boolean;
  'tasks.change_status': boolean;
  'tasks.view_internal_notes': boolean;
  
  // Projekty
  'projects.view': boolean;
  'projects.create': boolean;
  'projects.edit': boolean;
  'projects.delete': boolean;
  'projects.assign_users': boolean;
  
  // Zespoły i użytkownicy
  'teams.view': boolean;
  'teams.manage': boolean;
  'users.view': boolean;
  'users.manage': boolean;
  'clients.view': boolean;
  'clients.manage': boolean;
  
  // Komunikacja
  'comments.view_internal': boolean;
  'comments.create_internal': boolean;
  'comments.create_client': boolean;
  
  // Analityka i raporty
  'analytics.view': boolean;
  'analytics.global': boolean;
  'reports.view': boolean;
  'reports.create': boolean;
  
  // Ustawienia
  'settings.view': boolean;
  'settings.manage': boolean;
  'settings.global': boolean;
}

// Kontekst ról
interface RoleContextType {
  role: UserRole | null;
  permissions: Permissions;
  hasPermission: (permission: keyof Permissions) => boolean;
  hasRole: (role: UserRole | UserRole[]) => boolean;
  isAdmin: boolean;
  isUser: boolean;
  isClient: boolean;
}

const RoleContext = createContext<RoleContextType | undefined>(undefined);

// Hook useRole
export const useRole = (): RoleContextType => {
  const context = useContext(RoleContext);
  if (context === undefined) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
};

// Provider komponent
export const RoleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { state } = useApp();
  const [role, setRole] = useState<UserRole | null>(null);
  const [permissions, setPermissions] = useState<Permissions>({} as Permissions);

  // Funkcja definiująca uprawnienia dla każdej roli
  const getPermissionsForRole = (userRole: UserRole): Permissions => {
    const basePermissions: Record<UserRole, Permissions> = {
      admin: {
        // Zadania - TYLKO podgląd i zarządzanie, NIE tworzenie operacyjne
        'tasks.view': true,
        'tasks.create': false, // Admin nie tworzy zadań operacyjnych
        'tasks.edit': false,   // Admin nie edytuje zadań operacyjnych
        'tasks.delete': true,  // Może usuwać w celach administracyjnych
        'tasks.assign': true,  // Może przypisywać zadania użytkownikom
        'tasks.change_status': false, // Admin nie zmienia statusów operacyjnych
        'tasks.view_internal_notes': true,
        
        // Projekty - TYLKO zarządzanie, NIE tworzenie operacyjne
        'projects.view': true,
        'projects.create': false, // Admin nie tworzy projektów operacyjnych
        'projects.edit': false,   // Admin nie edytuje projektów operacyjnych
        'projects.delete': true,  // Może usuwać w celach administracyjnych
        'projects.assign_users': true, // Może przypisywać użytkowników do projektów
        
        // Zespoły i użytkownicy - GŁÓWNA ROLA ADMINA
        'teams.view': true,
        'teams.manage': true,
        'users.view': true,
        'users.manage': true,
        'clients.view': true,
        'clients.manage': true,
        
        // Komunikacja - wszystko
        'comments.view_internal': true,
        'comments.create_internal': true,
        'comments.create_client': true,
        
        // Analityka - globalna - GŁÓWNA ROLA ADMINA
        'analytics.view': true,
        'analytics.global': true,
        'reports.view': true,
        'reports.create': true,
        
        // Ustawienia - GŁÓWNA ROLA ADMINA
        'settings.view': true,
        'settings.manage': true,
        'settings.global': true,
      },
      
      user: {
        // Zadania - PEŁNA praca operacyjna - GŁÓWNA ROLA USERA
        'tasks.view': true,
        'tasks.create': true,    // User TWORZY zadania operacyjne
        'tasks.edit': true,      // User EDYTUJE swoje zadania
        'tasks.delete': true,    // User może usuwać swoje zadania
        'tasks.assign': true,    // User może przypisywać zadania w swoich projektach
        'tasks.change_status': true, // User zmienia statusy zadań
        'tasks.view_internal_notes': true,
        
        // Projekty - PEŁNA praca operacyjna - GŁÓWNA ROLA USERA  
        'projects.view': true,
        'projects.create': true,  // User TWORZY projekty operacyjne
        'projects.edit': true,    // User EDYTUJE swoje projekty
        'projects.delete': true,  // User może usuwać swoje projekty
        'projects.assign_users': true, // User może przypisywać ludzi do swoich projektów
        
        // Zespoły i użytkownicy - ograniczony dostęp
        'teams.view': true,      // Widzi zespoły do współpracy
        'teams.manage': false,   // NIE zarządza zespołami (to rola admina)
        'users.view': true,      // Widzi użytkowników do przypisania
        'users.manage': false,   // NIE zarządza użytkownikami (to rola admina)
        'clients.view': true,    // Widzi klientów dla projektów
        'clients.manage': false, // NIE zarządza klientami (to rola admina)
        
        // Komunikacja - wewnętrzna i z klientami
        'comments.view_internal': true,
        'comments.create_internal': true,
        'comments.create_client': true,
        
        // Analityka - własna produktywność
        'analytics.view': true,
        'analytics.global': false, // NIE widzi globalnych analityk (to rola admina)
        'reports.view': true,
        'reports.create': true,    // Może tworzyć raporty ze swojej pracy
        
        // Ustawienia - profil osobisty
        'settings.view': true,
        'settings.manage': false,  // NIE zarządza ustawieniami globalnymi
        'settings.global': false,
      },
      
      client: {
        // Zadania - TYLKO swoje projekty, ograniczone operacje
        'tasks.view': true,       // Widzi zadania w swoich projektach
        'tasks.create': true,     // Może zgłaszać nowe wymagania/zadania
        'tasks.edit': false,      // NIE edytuje zadań (tylko komentuje)
        'tasks.delete': false,    // NIE usuwa zadań
        'tasks.assign': false,    // NIE przypisuje zadań
        'tasks.change_status': true, // Może zatwierdzać/odrzucać (approve/reject)
        'tasks.view_internal_notes': false, // NIE widzi notatek wewnętrznych
        
        // Projekty - TYLKO swoje, ograniczone operacje
        'projects.view': true,    // Widzi swoje projekty
        'projects.create': false, // NIE tworzy projektów (to robi firma)
        'projects.edit': false,   // NIE edytuje projektów
        'projects.delete': false, // NIE usuwa projektów
        'projects.assign_users': false, // NIE przypisuje użytkowników
        
        // Zespoły i użytkownicy - BRAK dostępu administracyjnego
        'teams.view': false,      // NIE widzi zespołów wewnętrznych
        'teams.manage': false,
        'users.view': false,      // NIE widzi listy użytkowników
        'users.manage': false,
        'clients.view': false,    // NIE widzi innych klientów
        'clients.manage': false,
        
        // Komunikacja - TYLKO komunikacja z firmą
        'comments.view_internal': false, // NIE widzi komunikacji wewnętrznej
        'comments.create_internal': false, // NIE tworzy komentarzy wewnętrznych
        'comments.create_client': true,    // Może komunikować się z firmą
        
        // Analityka - BRAK dostępu do analityk
        'analytics.view': false,
        'analytics.global': false,
        'reports.view': false,    // Może tylko raporty końcowe projektów
        'reports.create': false,
        
        // Ustawienia - TYLKO profil osobisty
        'settings.view': true,    // Ustawienia profilu
        'settings.manage': false,
        'settings.global': false,
      }
    };

    return basePermissions[userRole];
  };

  // Aktualizuj rolę gdy zmieni się użytkownik
  useEffect(() => {
    if (state.user) {
      const userRole = (state.user as any).role as UserRole || 'user';
      setRole(userRole);
      setPermissions(getPermissionsForRole(userRole));
    } else {
      setRole(null);
      setPermissions({} as Permissions);
    }
  }, [state.user]);

  // Funkcje pomocnicze
  const hasPermission = (permission: keyof Permissions): boolean => {
    return permissions[permission] || false;
  };

  const hasRole = (requiredRole: UserRole | UserRole[]): boolean => {
    if (!role) return false;
    if (Array.isArray(requiredRole)) {
      return requiredRole.includes(role);
    }
    return role === requiredRole;
  };

  const value: RoleContextType = {
    role,
    permissions,
    hasPermission,
    hasRole,
    isAdmin: role === 'admin',
    isUser: role === 'user',
    isClient: role === 'client'
  };

  return (
    <RoleContext.Provider value={value}>
      {children}
    </RoleContext.Provider>
  );
};

// Komponenty pomocnicze dla prostego sprawdzania uprawnień
export const ProtectedComponent: React.FC<{
  permission?: keyof Permissions;
  role?: UserRole | UserRole[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
}> = ({ permission, role, children, fallback = null }) => {
  const { hasPermission, hasRole } = useRole();
  
  let hasAccess = true;
  
  if (permission && !hasPermission(permission)) {
    hasAccess = false;
  }
  
  if (role && !hasRole(role)) {
    hasAccess = false;
  }
  
  return hasAccess ? <>{children}</> : <>{fallback}</>;
};

// HOC dla chronienia całych komponentów
export const withRoleProtection = <P extends object>(
  Component: React.ComponentType<P>,
  permission?: keyof Permissions,
  role?: UserRole | UserRole[],
  FallbackComponent: React.ComponentType = () => <div>Brak uprawnień</div>
) => {
  return (props: P) => (
    <ProtectedComponent 
      permission={permission} 
      role={role} 
      fallback={<FallbackComponent />}
    >
      <Component {...props} />
    </ProtectedComponent>
  );
};

export default useRole;
